import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { UserCheck, Loader2 } from "lucide-react";

const inputCls = "border border-[#15233B]/15 rounded px-2.5 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1E73C9]/30";

export function AssignEmailedCard() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [showList, setShowList] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/emailed-applicants").then(({ data: d }) => setData(d)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => {
    load();
    api.get("/users").then(({ data: u }) => setUsers((u || []).filter((x) => x.role !== "field_agent"))).catch(() => setUsers([]));
  }, [load]);

  const run = async () => {
    const who = users.find((u) => u.id === userId)?.name;
    if (!window.confirm(`Make ${who} the Owner of all ${data.count} emailed applicants and assign their open tasks to ${who}?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/assign-emailed-applicants", { user_id: userId });
      toast.success(`Done — ${r.owners_changed} owner${r.owners_changed === 1 ? "" : "s"} changed, ${r.tasks_changed} open task${r.tasks_changed === 1 ? "" : "s"} reassigned`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="assign-emailed-card" className="bg-white border border-[#15233B]/10 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-[#1E73C9]" />
        <div className="text-sm font-extrabold text-[#15233B]">Assign emailed applicants</div>
      </div>
      <p className="text-xs text-[#15233B]/60">One-time cleanup for the Recruiting workspace: every non-archived applicant who has been emailed from the app gets a new <strong>Owner</strong>, and their <strong>open tasks</strong> are assigned to that person too. Each change is logged on the applicant's timeline.</p>
      {!data ? <Loader2 className="h-4 w-4 animate-spin text-[#15233B]/40" /> : (
        <>
          <div className="text-sm text-[#15233B]">
            <span data-testid="assign-emailed-count" className="font-extrabold">{data.count}</span> emailed applicant{data.count === 1 ? "" : "s"} found.
            {data.count > 0 && <button data-testid="assign-emailed-toggle-list" onClick={() => setShowList((x) => !x)} className="ml-2 text-xs font-bold text-[#1E73C9] hover:underline">{showList ? "Hide list" : "Show list"}</button>}
          </div>
          {showList && (
            <div data-testid="assign-emailed-list" className="max-h-60 overflow-y-auto border border-[#15233B]/10 rounded divide-y divide-[#15233B]/5">
              {data.cards.map((c) => (
                <div key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <span className="font-bold text-[#15233B]">{c.full_name}</span>
                  <span className="text-[#15233B]/45">{c.av_id}</span>
                  <span className="ml-auto text-[#15233B]/60">Owner: {c.owner_name}{c.open_tasks ? ` · ${c.open_tasks} open task${c.open_tasks === 1 ? "" : "s"}` : ""}</span>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="assign-emailed-user" value={userId} onChange={(e) => setUserId(e.target.value)} className={inputCls}>
              <option value="">Choose a person…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <button data-testid="assign-emailed-run" onClick={run} disabled={busy || !userId || !data.count}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-[#FFCB05] text-[#15233B] text-xs font-extrabold hover:bg-[#FBC02D] disabled:opacity-50">
              {busy && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Assign {data.count} applicant{data.count === 1 ? "" : "s"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
